import axios from "axios";
import { defineStore } from "pinia";
import { reactive, ref } from "vue";
import { useGlobalMessagesStore } from "./globalMessagesStore";
import { AuditDocument, AuditJob, AuditJobCreate, PagedList } from "@/audit.types";
import { handleError } from "./storeHelper";
import { serializeFilters, serializeSortBy } from "@/components/BTable/HelperFunctions";
import { BFilterableField, BFilterOptions, listOperations, textOperations } from "@/components/BFilterEditor/BFilterEditor.types";
import { BTableOptions } from "@/components/BTable/BTable.types";


export const useAuditJobStore = defineStore("auditJobStore", () => {
    const loading = ref<boolean>(false);
    const items = ref<AuditJob[]>();
    const globalMessages = useGlobalMessagesStore();
    const apiUrl = 'api/v1/audit/jobs';

    const filterOptions = ref<BFilterOptions>({
        filters: [],
    });
    const tableOptions = ref<BTableOptions>({
        totalItemCount: 0,
        itemsPerPage: 25,
        page: 1,
        sortBy: [],
        filterBy: [],
    } as BTableOptions);

    const filterableFields = reactive<BFilterableField[]>([
        {
            property: "jobId",
            label: "Job #",
            operations: textOperations,
            editorComponent: "text-editor",
        },
        {
            property: "createdDate",
            label: "Created Date",
            operations: listOperations,
            editorComponent: "list-editor",
        },
        {
            property: "status",
            label: "Status",
            operations: listOperations,
            editorComponent: "list-editor",
        },
    ]);

    const list = async (auditId: number) => {

        try {
            loading.value = true;

            let filterParam = serializeFilters(filterOptions.value.filters, filterableFields);
            let sortParam = serializeSortBy(tableOptions.value.sortBy);

            const response = await axios.get<PagedList<AuditJob>>(`${apiUrl}/${auditId}`, {
                params: {
                    limit: tableOptions.value.itemsPerPage,
                    offset: tableOptions.value.itemsPerPage * (tableOptions.value.page - 1),
                    sort: sortParam,
                    filter: filterParam,
                }
            });
            items.value = response.data.data;
            tableOptions.value.totalItemCount = response.data.total;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const create = async (item: AuditJobCreate): Promise<AuditJobCreate> => {
        try {
            loading.value = true;
            const response = await axios.put<AuditJobCreate>(apiUrl, item);
            globalMessages.push({ message: "Job created successfully.", type: "success" })
            return response.data;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const run = async (id: number): Promise<void> => {
        try {
            loading.value = true;
            const response = await axios.post<AuditJobCreate>(`${apiUrl}/actions/${id}/run`, id);
            globalMessages.push({ message: "Job run successfully.", type: "success" })

        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const deleteItem = async (id: number): Promise<void> => {
        try {
            loading.value = true;
            const response = await axios.delete<AuditDocument>(`${apiUrl}/${id}`);
            globalMessages.push({ message: "Job deleted successfully.", type: "success" })
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    const getById = async (id: number): Promise<AuditJob> => {

        try {
            loading.value = true;
            const response = await axios.get<AuditJob>(`${apiUrl}/${id}`);
            return response.data;
        }
        catch (error) {
            handleError(error);
            throw error;
        }
        finally {
            loading.value = false;
        }
    }

    return {
        tableOptions,
        filterOptions,
        filterableFields,
        loading,
        create,
        deleteItem,
        items,
        getById,
        run,
        list
    };
})